import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { LanguageCode } from "../../models/enums/LanguageCode";
import { TranslatableFieldUiState } from "../../hooks/useTranslatableForm";
import { FrenchFlag, BritishFlag } from "../language/FlagIcons";
import StaleTranslationBadge from "../common/StaleTranslationBadge";
import { zoullooColors } from "../../theme";

interface LanguageOption {
  code: LanguageCode;
  label: string;
  Flag: React.FC<{ width?: number; height?: number }>;
}

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: LanguageCode.Fr, label: "Français", Flag: FrenchFlag },
  { code: LanguageCode.En, label: "English", Flag: BritishFlag },
];

interface TranslatableFieldWrapperProps {
  label: string;
  editingLanguage: LanguageCode;
  onLanguageChange: (lang: LanguageCode) => void;
  required?: boolean;
  /** Shows an "outdated translation" badge when the human translation for this field is stale. */
  isStale?: boolean;
  /**
   * UI state of the field in the DISPLAYED language (useTranslatableForm.getFieldUiState).
   * Drives the permanent status chip, the read-only lock on AI-managed translations and the
   * available action. Omitted → plain wrapper (read-only viewers keep their legacy rendering).
   */
  uiState?: TranslatableFieldUiState;
  /**
   * The talent's input language (useTranslatableForm.sourceLanguage): names the language the
   * AI-first note announces ("the English version will be generated…").
   */
  sourceLanguage?: LanguageCode;
  /**
   * AI-first gate (useTranslatableForm.isLanguageSelectorVisible): when false the FR/EN selector
   * is hidden — the non-source translation has nothing to show yet. Default true.
   */
  showLanguageSelector?: boolean;
  /**
   * Whether to announce the upcoming generation ("the X version will be generated after
   * saving") — useTranslatableForm.isAiFirstNoteVisible. Kept separate from the selector gate:
   * the note is a promise, and it must never show for a NON-TARGET language where nothing will
   * ever be generated. Default false.
   */
  showAiFirstNote?: boolean;
  /** True while the field is explicitly unlocked (draft mode) in the displayed language. */
  isDraft?: boolean;
  /** Called when the user clicks "write my own version" on an AI-managed translation. */
  onBeginDraft?: () => void;
  /**
   * Called when the user cancels their unlocked draft (restores the AI value, no trace).
   * There is deliberately NO field-level save: once unlocked, the step's real save button is
   * the only save — a local "save my version" would read as a real one and mislead the talent.
   */
  onCancelDraft?: () => void;
  /**
   * Whether the hand-back action is offered for the displayed language (factual: that language is
   * human AND another live human source remains AND it is not the source language).
   */
  canHandBackToAi?: boolean;
  /** Whether a regeneration is currently in flight for this field. */
  isRegenerating?: boolean;
  /** Called when the user opts in to AI auto-translation for this field. */
  onEnableAi?: () => void;
  /**
   * Token folded into the children's remount key. Pass a value that changes when the displayed
   * content becomes available (e.g. useTranslatableForm's translationsLoaded): uncontrolled
   * inputs (TextField defaultValue, Tiptap) only read their value on mount, so without this a
   * language switched to BEFORE its translations query resolved would show empty inputs forever.
   * Boolean is accepted for direct wiring of translationsLoaded.
   */
  contentReadyToken?: React.Key | boolean;
  children: React.ReactNode;
}

// The source language carries NO chip: it is simply the talent's text, the label above the
// field says everything — a "your text" badge on every field is noise.
const CHIP_BY_STATE: Record<
  Exclude<TranslatableFieldUiState, "source">,
  { labelKey: string; color: "default" | "info" | "success" | "warning" }
> = {
  ai: { labelKey: "translations.state.aiManaged", color: "info" },
  human: { labelKey: "translations.state.humanManaged", color: "success" },
  humanStale: { labelKey: "translations.state.humanStale", color: "warning" },
};

const TranslatableFieldWrapper: React.FC<TranslatableFieldWrapperProps> = ({
  label,
  editingLanguage,
  onLanguageChange,
  required,
  isStale,
  uiState,
  sourceLanguage,
  showLanguageSelector,
  showAiFirstNote,
  isDraft,
  onBeginDraft,
  onCancelDraft,
  canHandBackToAi,
  isRegenerating,
  onEnableAi,
  contentReadyToken,
  children,
}) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // "Re-translation in progress" applies to whichever language is being handed back to the AI.
  const showRegenerating = !!isRegenerating;

  const selectorVisible = showLanguageSelector ?? true;

  // The language the AI-first note announces: the non-source one (the one being generated).
  const generatedLanguage =
    (sourceLanguage ?? editingLanguage) === LanguageCode.Fr
      ? LanguageCode.En
      : LanguageCode.Fr;

  // An AI-managed translation is read-only until explicitly unlocked — no silent takeover.
  const aiLocked = uiState === "ai" && !isDraft;

  // "Write my own version" only makes sense once the AI cell exists (selectorVisible tracks
  // exactly that for talent-owned fields — AI-first: nothing to personalise before generation).
  const showUnlockButton = aiLocked && !!onBeginDraft && selectorVisible;

  // Hand-back is NEVER offered on the source language (its state is "source", not "human").
  const showHandBackButton =
    (uiState === "human" || uiState === "humanStale") && !!canHandBackToAi;

  const chip = uiState && uiState !== "source" ? CHIP_BY_STATE[uiState] : undefined;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box component="span" sx={{ fontWeight: 500, fontSize: "0.875rem" }}>
            {label}
            {required && " *"}
          </Box>
          {isStale && <StaleTranslationBadge />}
        </Box>
        {selectorVisible && (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {LANGUAGE_OPTIONS.map(({ code, label: langLabel, Flag }) => {
              const isActive = editingLanguage === code;
              return (
                <Tooltip key={code} title={langLabel}>
                  <IconButton
                    size="small"
                    onClick={() => onLanguageChange(code)}
                    sx={{
                      borderRadius: 1,
                      opacity: isActive ? 1 : 0.35,
                      filter: isActive ? "none" : "grayscale(80%)",
                      p: 0.5,
                      transition: "all 0.15s ease",
                      "&:hover": {
                        opacity: isActive ? 1 : 0.7,
                        filter: "none",
                      },
                    }}
                  >
                    <Flag width={20} height={15} />
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>
      {/* Status row: while a field is being re-translated, show the "in progress" banner.
          Otherwise show the permanent state chip plus the single action that applies:
          - "ai": read-only + "write my own version" (or draft save/cancel once unlocked);
          - "human"/"humanStale": hand back to / replace by the automatic translation;
          - "source": no action ever (the talent's input language is not hand-backable). */}
      {showRegenerating ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            {t("translations.autoTranslateInProgress")}
          </Typography>
        </Box>
      ) : chip ? (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {uiState === "ai" && isDraft ? (
            // Unlocked: say plainly that nothing is saved yet — the step's real save button is
            // the only save (a field-level "save my version" would read as a real one).
            <Chip
              size="small"
              variant="outlined"
              label={t("translations.state.draftUnsaved")}
              sx={{ borderStyle: "dashed", color: "text.secondary" }}
            />
          ) : uiState === "ai" ? (
            <Chip
              size="small"
              label={`✦ ${t(chip.labelKey)}`}
              sx={{
                backgroundColor: zoullooColors.background.menu,
                color: zoullooColors.secondary.main,
                fontWeight: 600,
                height: 28,
                fontSize: "0.8125rem",
              }}
            />
          ) : (
            <Chip
              size="small"
              variant="outlined"
              color={chip.color}
              label={t(chip.labelKey)}
            />
          )}
          {showUnlockButton && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={onBeginDraft}
              // Compact: sits next to the state chip, so it must read as its peer, not tower over
              // it. The explicit height is required — the theme forces height: 50px on ALL
              // buttons (theme.ts MuiButton styleOverrides), so padding alone changes nothing.
              sx={{ height: 28, py: 0, px: 1.5, minWidth: 0, fontSize: "0.8125rem" }}
            >
              {t("translations.writeMyOwnVersion")}
            </Button>
          )}
          {uiState === "ai" && isDraft && (
            <Button size="small" variant="text" onClick={onCancelDraft}>
              {t("common.cancel")}
            </Button>
          )}
          {showHandBackButton && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={() => setConfirmOpen(true)}
              // Same compact treatment as the unlock button: a real button, peer of the chip
              // (a text variant only reads as a button on hover). Height beats the theme's 50px.
              sx={{ height: 28, py: 0, px: 1.5, minWidth: 0, fontSize: "0.8125rem" }}
            >
              {uiState === "humanStale"
                ? t("translations.replaceWithAutoTranslation")
                : t("translations.handBackToAi")}
            </Button>
          )}
        </Box>
      ) : null}
      {/* AI-first note: the non-source translation doesn't exist yet AND will actually be
          generated — announce it. Driven by its own prop (not the selector gate): the selector
          is also hidden for non-target languages, where this promise would be a lie. */}
      {showAiFirstNote && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {t("translations.aiFirstNote", {
            language: t(`translations.languageName${generatedLanguage}`),
          })}
        </Typography>
      )}
      {/* Keep the field visible but greyed out and locked while the AI re-translates it, and
          read-only (lighter) while an AI-managed translation is not explicitly unlocked. The key
          remounts the field when the displayed language changes, when a regeneration ends
          (regenerating → idle) or when a draft is cancelled/committed: the value to display then
          re-initialises uncontrolled inputs (TextField defaultValue) and the Tiptap editor (which
          only reads `content` on create). This is what makes the new translation appear live,
          without a reload, for every translatable field. The key is stable during normal typing,
          so editing is never disrupted.
          contentReadyToken covers the remaining hole: when the user switches language BEFORE that
          language's translations query resolved, the inputs mount empty and the values arriving
          later would never show (typing over them would even overwrite the invisible content on
          save). The token changes once when the data lands → one extra remount that re-initialises
          the inputs from the hook's state; values the user typed live in that same state, so they
          are preserved across the remount. */}
      <Box
        key={`${editingLanguage}-${showRegenerating ? "regenerating" : "idle"}-${
          isDraft ? "draft" : "committed"
        }-${contentReadyToken ?? ""}`}
        sx={
          showRegenerating
            ? // Transient (a few seconds): the full pointer lock is acceptable here.
              { opacity: 0.5, pointerEvents: "none" }
            : aiLocked
            ? // Visual dim ONLY — the input lock itself is carried by the inputs' NATIVE
              // read-only mode (useTranslatableForm.isFieldReadOnly → TextField
              // InputProps.readOnly / Tiptap editable={false}). pointer-events:none here also
              // killed the editor's internal scrolling, text selection/copy and accessibility
              // on long content (an AI-translated presentation was unreadable past one screen).
              { opacity: 0.7 }
            : undefined
        }
        data-testid="translatable-field-content"
      >
        {children}
      </Box>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("translations.autoTranslateConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("translations.autoTranslateConfirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setConfirmOpen(false);
              onEnableAi?.();
            }}
          >
            {t("translations.autoTranslateConfirmAction")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TranslatableFieldWrapper;
